/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */

import { hydrateRoot, createRoot } from 'react-dom/client';
import consoleIgnoreCriteria from './src/data/consoleIgnoreCriteria';
import wrapWithProvider from './wrap-with-provider';
import { getLangFromPathname } from './src/utils/language';

// Import all global CSS with consistent ordering
import './src/styles/global-css.js';

export const wrapRootElement = wrapWithProvider;

export const replaceHydrateFunction = () => (Component, el) => {
  const useHydrate = el && el?.children?.length;

  const hydrationOptions = {
    onRecoverableError: (error) => {
      // Only log in development and if not in ignore criteria
      if (process.env.NODE_ENV === 'development') {
        const errorMessage = error?.message || '';
        const shouldIgnore = consoleIgnoreCriteria.error.some((ignoreString) => errorMessage.includes(ignoreString));

        if (!shouldIgnore) {
          console.error('Recovered from error during hydration: ', error);
        }
      }
    }
  };

  try {
    if (useHydrate) {
      const root = hydrateRoot(el, Component, hydrationOptions);

      return () => {
        try {
          root.unmount();
        } catch (unmountError) {
          console.warn('Error during unmount:', unmountError);
        }
      };
    }

    const root = createRoot(el, hydrationOptions);

    root.render(Component);

    return () => {
      try {
        root.unmount();
      } catch (unmountError) {
        console.warn('Error during unmount:', unmountError);
      }
    };
  } catch (error) {
    console.error('Caught hydration error: ', error);

    // Fallback: try to create a new root if hydration fails
    try {
      const fallbackRoot = createRoot(el, hydrationOptions);
      fallbackRoot.render(Component);
      return () => {
        try {
          fallbackRoot.unmount();
        } catch (unmountError) {
          console.warn('Error during fallback unmount:', unmountError);
        }
      };
    } catch (fallbackError) {
      console.error('Fallback root creation failed:', fallbackError);
      return () => {}; // Return empty cleanup function as fallback
    }
  }
};

export const onRouteUpdate = () => {
  window.locations = window.locations || [document.referrer];

  const currentPath = window.location.pathname;

  if (window.locations[window.locations.length - 1] !== currentPath) {
    window.locations.push(currentPath);
  }

  window.previousPath = window.locations[window.locations.length - 2];

  document.documentElement.lang = getLangFromPathname(currentPath);
};

if (process.env.NODE_ENV === 'development') {
  const overrideConsoleMethod = (methodName, originalMethod) => {
    return (...args) => {
      if (args[0] && typeof args[0] === 'string' && consoleIgnoreCriteria[methodName].some((ignoreString) => args[0].includes(ignoreString))) {
        return;
      }
      originalMethod(...args);
    };
  };

  console.warn = overrideConsoleMethod('warn', console.warn);
  console.error = overrideConsoleMethod('error', console.error);
}
